import React, { useState, useEffect } from 'react'
import styled from '@emotion/styled'
import { message } from 'antd'
import { useNavigate } from 'react-router-dom'
import { FETCH_LIGHTSPEED_LOCATIONS, GET_LIGHTSPEED_STORES } from './queries'
import { GET_MERCHANT_INTEGRATIONS } from '../../queries'
import { useLazyQuery, useQuery } from '@apollo/client'
import { LightspeedBusiness, LightspeedStore } from './types'
import Loading from 'components/Utils/Loading'
import { StepStatus } from 'hooks/useStep'
import BusinessSection from './BusinessSection'
import LocationSection from './LocationSection'
import LocationModal from './LocationModal'
import { useSession } from 'packages/@slerp/accounts'
import {
  MerchantLocationResponseType,
  QUERY_MERCHANT_LOCATIONS
} from 'components/Locations/LocationQueries'
import { Store } from '@slerp/controls'
import CommonHeader from '../Common/CommonHeader'
import ConnectSection from './ConnectSection'
import { PartnerName, isPartner } from '../../constants'

const ManageLightspeed = () => {
  const navigate = useNavigate()
  const { merchant } = useSession()

  const [hasIntegrationConnected, setHasIntegrationConnected] = useState(false)
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false)

  const [isInitialized, setIsInitialized] = useState(false)
  const [selectedBusiness, setSelectedBusiness] =
    useState<LightspeedBusiness | null>(null)
  const [selectedLocation, setSelectedLocation] =
    useState<LightspeedStore | null>(null)

  const [currentStep, setCurrentStep] = useState<number>(1)

  const [stores, setStores] = useState<Store[]>([])
  const [connectedLocations, setConnectedLocations] = useState<
    LightspeedStore[]
  >([])
  const [businesses, setBusinesses] = useState<LightspeedBusiness[]>([])

  const { loading: loadingIntegrations } = useQuery(GET_MERCHANT_INTEGRATIONS, {
    variables: { merchantId: merchant?.id },
    onCompleted: (data) => {
      setHasIntegrationConnected(
        data?.partners_merchants?.some(
          (partner: { partner: { name: string } }) =>
            isPartner(partner.partner.name, PartnerName.LIGHTSPEED)
        )
      )
    }
  })

  const { loading: loadingStores } = useQuery<MerchantLocationResponseType>(
    QUERY_MERCHANT_LOCATIONS,
    {
      variables: { slug: merchant?.slug },
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        const stores = data?.merchants[0].stores.filter(
          (store) => !store.archived_at
        )
        setStores(stores)
      }
    }
  )

  const { loading: loadingBusinesses } = useQuery(FETCH_LIGHTSPEED_LOCATIONS, {
    onCompleted: (data) => {
      setBusinesses(data.fetchLightspeedIds)
    },
    onError: () => {
      handleError()
    }
  })

  const [fetchConnectedLocations, { loading: loadingLightspeedStores }] =
    useLazyQuery(GET_LIGHTSPEED_STORES, {
      fetchPolicy: 'no-cache',
      variables: { merchantId: merchant?.id },
      onCompleted: (data) => {
        const connectedLocations = data?.stores.filter(
          (store: LightspeedStore) =>
            !!store.store_integration_settings.lightspeed_business_id
        )
        setConnectedLocations(connectedLocations || [])
      }
    })

  useEffect(() => {
    if (isInitialized) {
      return
    }

    setIsInitialized(
      loadingBusinesses && loadingStores && loadingLightspeedStores
    )
  }, [loadingBusinesses, loadingStores, loadingLightspeedStores])

  useEffect(() => {
    fetchConnectedLocations()
  }, [])

  useEffect(() => {
    if (!connectedLocations.length || !businesses.length) {
      return
    }

    const business = businesses.find(
      (business) =>
        business.id ===
        connectedLocations[0].store_integration_settings.lightspeed_business_id
    )

    if (business) {
      setSelectedBusiness(business)
      setCurrentStep(2)
    }
  }, [connectedLocations, businesses])

  const handleBusinessFinish = (business: LightspeedBusiness | null) => {
    if (business) {
      setCurrentStep(2)
    }

    setSelectedBusiness(business)
  }

  const handleDisconnectBusiness = () => {
    setSelectedBusiness(null)
    setCurrentStep(1)
  }

  const handleError = () => {
    if (!hasIntegrationConnected || loadingIntegrations) {
      return
    }

    message.destroy()
    message.error('Your session has expired. Please log in again.')

    navigate('/integrations')
  }

  const handleSelectLocation = (location: LightspeedStore) => {
    setSelectedLocation(location)
    setIsConnectModalOpen(true)
  }

  const handleConnectStore = () => {
    setIsConnectModalOpen(true)
    setSelectedLocation(null)
  }

  const handleConnectCancel = () => {
    setIsConnectModalOpen(false)
    setSelectedLocation(null)
  }

  const handleConnectOk = () => {
    setIsConnectModalOpen(false)
    setSelectedLocation(null)
    fetchConnectedLocations()
  }

  const handleBulkDisconnect = () => {
    fetchConnectedLocations()
  }

  const handleBulkSyncMenu = () => {
    fetchConnectedLocations()
  }

  if (loadingIntegrations) {
    return <Loading />
  }

  if (!hasIntegrationConnected) {
    return (
      <Root>
        <CommonHeader
          title='Connect Lightspeed'
          onBack={() => navigate('/integrations')}
          helpLink='https://support.slerp.com/knowledge/how-do-i-connect-to-lightspeed-k-series'
        />
        <ConnectSection />
      </Root>
    )
  }

  if (
    loadingBusinesses ||
    loadingStores ||
    (!isInitialized && loadingLightspeedStores)
  ) {
    return <Loading />
  }

  return (
    <Root>
      <CommonHeader
        title='Connect Lightspeed'
        onBack={() => navigate('/integrations')}
        helpLink='https://support.slerp.com/knowledge/how-do-i-connect-to-lightspeed-k-series'
      />
      <BusinessSection
        businesses={businesses}
        selectedBusiness={selectedBusiness}
        onFinish={handleBusinessFinish}
        stepStatus={
          currentStep === 1 ? StepStatus.active : StepStatus.completed
        }
        onDisconnect={handleDisconnectBusiness}
        canDisconnect={connectedLocations.length === 0}
      />
      {currentStep > 1 && !!selectedBusiness && (
        <LocationSection
          connectedLocations={connectedLocations}
          onConnect={handleConnectStore}
          onSelect={handleSelectLocation}
          stepStatus={StepStatus.active}
          onBulkDisconnect={handleBulkDisconnect}
          onBulkSyncMenu={handleBulkSyncMenu}
          isLoading={loadingLightspeedStores}
        />
      )}

      {!!selectedBusiness && (
        <LocationModal
          selectedLocation={selectedLocation}
          onCancel={handleConnectCancel}
          onOk={handleConnectOk}
          isOpen={isConnectModalOpen}
          selectedBusiness={selectedBusiness}
          connectedLocations={connectedLocations}
          stores={stores}
          onError={handleError}
        />
      )}
    </Root>
  )
}

const Root = styled.div`
  display: flex;
  gap: 24px;
  flex-direction: column;
`

export default ManageLightspeed
